// kufer-course-card.js
//
// Gemeinsame Render-Logik fuer eine einzelne Kurs-Karte, genutzt von
// kufer-search.js und kufer-kurs-loader.js. Beide bauten diese Logik vorher
// unabhaengig voneinander (Status/Datum/Titel/Tags/Favoriten-Anzeige), was
// bereits einmal auseinandergelaufen ist (z.B. unterschiedliche Fachbereich-Links
// fuer private Kurse). Aenderungen an der Kurs-Card-Darstellung muessen jetzt
// nur noch an einer Stelle gepflegt werden.

import { slugify } from "./kufer-utils";
import { updateFavButtonVisual } from "./kufer-favorites";

/**
 * Klont das Template und befuellt es mit den Daten eines Kurses.
 * Haengt KEINEN Klick-Listener fuer den Favoriten-Button an - das bleibt
 * Sache des Aufrufers, da sich das Verhalten nach dem Togglen unterscheidet
 * (z.B. Entfernen der Karte auf der Merkliste-Seite).
 *
 * @param {HTMLElement} template - Das (verborgene) Karten-Template.
 * @param {object} kurs - Ein Kurs-Objekt aus der Kufer-API.
 * @param {object} [options]
 * @param {object} [options.fachbereicheData] - Map fachb-Code -> Anzeigename.
 * @param {string} [options.agId] - Arbeitgeber-ID, wird als ?ag=... an den Permalink angehaengt.
 * @param {string} [options.token] - Privates Session-Token, wird als ?token=... an den Permalink angehaengt.
 * @returns {{ courseEntry: HTMLElement, favBtn: HTMLElement|null }}
 */
export function populateCourseCard(template, kurs, options = {}) {
  const { fachbereicheData = {}, agId = "", token = "" } = options;

  const courseEntry = template.cloneNode(true);
  courseEntry.style.display = "";
  courseEntry.classList.remove("course-template");

  // --- Status ---
  let statusClass = "course-entry__status--available",
    statusIconText = "check",
    statusTitle = "Verfügbar";
  if (String(kurs.status) === "3") {
    statusClass = "course-entry__status--not-available";
    statusIconText = "close";
    statusTitle = "Ausgebucht";
  } else if (String(kurs.status) === "2") {
    statusClass = "course-entry__status--few-available";
    statusTitle = "Wenige Plätze verfügbar";
  }
  const statusElement = courseEntry.querySelector(".course-entry__status");
  if (statusElement) {
    statusElement.className = `course-entry__status ${statusClass}`;
    statusElement.title = statusTitle;
    const statusIcon = statusElement.querySelector(".material-icons-outlined");
    if (statusIcon) statusIcon.textContent = statusIconText;
  }

  // --- Datum ---
  const dateWrapper = courseEntry.querySelector("[data-date-wrapper]");
  if (kurs.beginndat) {
    const dateObject = new Date(kurs.beginndat);
    if (!isNaN(dateObject)) {
      const formattedDate = new Intl.DateTimeFormat("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(dateObject);
      const dateEl = courseEntry.querySelector("[data-date]");
      if (dateEl) dateEl.textContent = formattedDate;
      const timeEl = courseEntry.querySelector("[data-time]");
      if (timeEl) timeEl.textContent = kurs.beginnuhr ? `${kurs.beginnuhr} Uhr` : "";
    } else if (dateWrapper) {
      dateWrapper.style.display = "none";
    }
  } else if (dateWrapper) {
    dateWrapper.style.display = "none";
  }

  // --- Titel & Links ---
  const titleSlug = slugify(kurs.haupttitel);
  const privateSlug = kurs.internetaktiv === "F" ? "priv-" : "";
  const basePermalink = `/kurs/${titleSlug}-${privateSlug}${kurs.knr}/`;

  // Bei privaten Kursen muss das Session-Token mit an die Detailseite, sonst
  // weist kurs.php den Aufruf zurueck (siehe mmmake_kufer_check_proxy_token-
  // Pendant dort). So ist die Detailseite nur ueber einen Link aus dieser
  // Liste heraus erreichbar, nicht per direkt geratener/geteilter URL.
  const queryParts = [];
  if (agId) queryParts.push(`ag=${encodeURIComponent(agId)}`);
  if (token) queryParts.push(`token=${encodeURIComponent(token)}`);
  const permalink = queryParts.length
    ? `${basePermalink}?${queryParts.join("&")}`
    : basePermalink;

  courseEntry.href = basePermalink;

  const knrEl = courseEntry.querySelector("[data-knr]");
  if (knrEl) {
    knrEl.textContent = kurs.knr;
    knrEl.href = permalink;
  }
  const titleEl = courseEntry.querySelector("[data-title]");
  if (titleEl) {
    titleEl.textContent = kurs.haupttitel;
    titleEl.href = permalink;
  }
  const subtitleEl = courseEntry.querySelector("[data-subtitle]");
  if (subtitleEl) {
    subtitleEl.textContent = kurs.hauptuntertitel;
    subtitleEl.href = permalink;
  }

  // --- Tags (Fachbereich + Kategorien) ---
  const tagsContainer = courseEntry.querySelector("[data-tags-container]");
  if (tagsContainer) {
    let tagsHtml = "";
    if (kurs.fachb && fachbereicheData[kurs.fachb]) {
      const permalinkFachbereich = `/kurse/?fachb=${kurs.fachb}`;
      tagsHtml +=
        kurs.internetaktiv === "F"
          ? `<span class="tag course-entry__tag">${fachbereicheData[kurs.fachb]}</span>`
          : `<a href="${permalinkFachbereich}" class="tag course-entry__tag">${fachbereicheData[kurs.fachb]}</a>`;
    }
    if (kurs.kategorien) {
      Object.values(kurs.kategorien).forEach((kategorie) => {
        tagsHtml += `<div class="tag course-entry__tag">${kategorie}</div>`;
      });
    }
    tagsContainer.innerHTML = tagsHtml;
  }

  // --- Favoriten (nur visueller Status, Klick-Handling macht der Aufrufer) ---
  const favBtn = courseEntry.querySelector("[data-fav-btn]");
  if (favBtn) {
    updateFavButtonVisual(favBtn, kurs.knr);
  }

  return { courseEntry, favBtn };
}
